import { StaffNote } from 'restaurantAdmin/components/staffNote/StaffNote';
import { GuestInfo } from 'restaurantAdmin/components/guestInfo/GuestInfo';
import { GuestNotes } from 'restaurantAdmin/components/guestNotes/GuestNotes';
import { GuestTags } from 'restaurantAdmin/components/guestTags/GuestTags';
import { NotesToRestaurant } from 'restaurantAdmin/components/notesToRestaurant/NotesToRestaurant';
import { OrderDetails } from 'restaurantAdmin/components/orderDetails/OrderDetails';
import { ReservationGuestSheetReceipt } from 'restaurantAdmin/components/reservationGuestSheetReceipt/ReservationGuestSheetReceipt';
import { ReservationGuestSheetStatuses } from 'restaurantAdmin/components/reservationGuestSheetStatuses/ReservationGuestSheetStatuses';
import { AssignedServer } from 'restaurantAdmin/components/server/AssignedServer';
import { isReservationFinished } from 'restaurantAdmin/reservations/reservationUtils';
import { INACTIVE_WALK_IN_STATUSES } from '@shared/types/walkIns';
import { type HostFloorPlanData } from 'restaurantAdmin/floorPlans/apiHelpers';
import { FloorPlan } from '@components/floorPlan/FloorPlan';
import { floorPlanTablesRendererFactory } from '@components/floorPlan/floorPlanTablesRendererFactory';
import { IconButton } from '@components/iconButton/IconButton';
import { Modal } from '@components/modal/Modal';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { getFullName } from '@shared/utils/formatName';
import cx from 'classnames';
import { intersection } from 'lodash-es';
import type { ReactNode } from 'react';
import { generatePath, Link } from 'react-router-dom';
import typography from '~styles/typography.scss';
import type { OccupantType } from '../../apiHelpers';
import {
  hasEvent,
  isReservation,
  isWalkIn,
  ServiceOccupant,
  type ServiceReservation,
} from '../apiHelpers';
import { HotelLinkSection } from '../hotelLink/HotelLinkSection';
import { useFetchOccupantDetails } from '../hooks/useFetchOccupantDetails';
import { useReservationServiceContext } from '../state/ReservationServiceContext';
import styles from './OccupantDetails.scss';
import { OccupantDetailsActions } from './OccupantDetailsActions';
import { OccupantDetailsHeader } from './OccupantDetailsHeader';
import { useServiceSearchParams } from '../hooks/useServiceSearchParams';
import { deriveTableDisplayName } from '@shared/utils/deriveTableDisplayName';
import { Icon } from '@components/icon/Icon';
import { ActivityLogDrawer } from './ActivityLogDrawer';
import { useFeatureFlagContext } from 'restaurantAdmin/featureFlags';
import { GUEST_BOOK_GUEST_PATH } from 'restaurantAdmin/paths';
import { GuestReservationCancellationFee } from 'restaurantAdmin/guestBook/GuestReservationCancellationFee';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';

const OccupantDetailsFloorPlan = ({
  floorPlan,
  reservation,
}: {
  floorPlan: HostFloorPlanData;
  reservation: ServiceReservation;
}) => (
  <FloorPlan
    backgroundSrc={floorPlan.backgroundSrc}
    disableControls
    floorPlanTablesRenderer={floorPlanTablesRendererFactory({
      tables: floorPlan.floorPlanTables,
      showHoverStroke: false,
      calculateIsHighlighted: (table) => {
        const listingsOnTable = table.listings.map((l) => l.id);
        const listingsOnReservation = reservation.tableAssignments.map(
          (ta) => ta.listingId,
        );

        return intersection(listingsOnTable, listingsOnReservation).length > 0;
      },
    })}
  />
);

type OccupantDetailsProps = {
  allowActivationOfSeatMode: boolean;
};

export const OccupantDetailsDrawer = (props: OccupantDetailsProps) => {
  const { allowActivationOfSeatMode } = props;
  const { occupantId, occupantType } = useServiceSearchParams();
  const { refreshOccupants, floorPlansBySelectedDate } =
    useReservationServiceContext();
  const { occupant, invalidate } = useFetchOccupantDetails({
    id: occupantId as string,
    type: occupantType as OccupantType,
  });

  const {
    isOpen: isExpandFloorPlanModalOpen,
    open: openExpandFloorPlanModal,
    close: closeExpandFloorPlanModal,
  } = useIsOpen();

  if (!occupant) {
    return null;
  }

  return (
    <OccupantDetailsBase
      occupant={occupant}
      allowActivationOfSeatMode={allowActivationOfSeatMode}
      floorPlansBySelectedDate={floorPlansBySelectedDate}
      refetchOccupant={invalidate}
      refetchAllOccupants={refreshOccupants}
      isExpandFloorPlanModalOpen={isExpandFloorPlanModalOpen}
      openExpandFloorPlanModal={openExpandFloorPlanModal}
      closeExpandFloorPlanModal={closeExpandFloorPlanModal}
    />
  );
};

export interface OccupantDetailsBaseProps {
  occupant: ServiceOccupant;
  allowActivationOfSeatMode: boolean;
  floorPlansBySelectedDate: HostFloorPlanData[];
  refetchOccupant: () => void;
  refetchAllOccupants: () => void;
  isExpandFloorPlanModalOpen: boolean;
  openExpandFloorPlanModal: () => void;
  closeExpandFloorPlanModal: () => void;
}

export const OccupantDetailsBase = ({
  occupant,
  allowActivationOfSeatMode,
  floorPlansBySelectedDate,
  refetchOccupant,
  refetchAllOccupants,
  isExpandFloorPlanModalOpen,
  openExpandFloorPlanModal,
  closeExpandFloorPlanModal,
}: OccupantDetailsBaseProps) => {
  const {
    isOpen: isActivityLogOpen,
    open: openActivityLog,
    close: closeActivityLog,
  } = useIsOpen();

  const { isEnabled } = useFeatureFlagContext();
  const { hotelIntegrationEnabled } = useRestaurant();

  const occupantId = occupant.id;
  const occupantType = occupant.type;

  const floorPlanToDisplay =
    isReservation(occupant) && occupant.wasTableChosenAtTimeOfBooking
      ? floorPlansBySelectedDate.find(
          (fp) => fp.id === occupant.listingFloorPlanId,
        )
      : null;

  const occupantDisplayName = isReservation(occupant)
    ? getFullName(occupant.guest.firstName, occupant.guest.lastName)
    : getFullName(occupant.firstName, occupant.lastName) || 'Walk-In';

  let guestBookGuestId: string | null = null;
  if (isReservation(occupant)) {
    guestBookGuestId = occupant.guest.id;
  } else if (isWalkIn(occupant) && occupant.guest) {
    guestBookGuestId = occupant.guest.id;
  }

  const occupantDisplayNameGuestLink: ReactNode = guestBookGuestId ? (
    <Link
      rel="noopener noreferrer"
      target="_blank"
      to={generatePath(GUEST_BOOK_GUEST_PATH, {
        guestId: guestBookGuestId,
      })}
      className={styles.guestBookNameLink}
    >
      {occupantDisplayName}
    </Link>
  ) : (
    occupantDisplayName
  );

  return (
    <div data-testid="occupant-details-drawer">
      <OccupantDetailsHeader
        occupant={occupant}
        fetchOccupant={refetchOccupant}
      />
      <div className={styles.container}>
        <div className={styles.column}>
          <GuestInfo
            className={styles.section}
            name={occupantDisplayNameGuestLink}
            email={isReservation(occupant) ? occupant?.guest?.email : undefined}
            phone={isReservation(occupant) ? occupant?.guest?.phone : undefined}
          />

          {isReservation(occupant) && (
            <>
              {hotelIntegrationEnabled && (
                <HotelLinkSection
                  className={styles.section}
                  onUpdate={refetchAllOccupants}
                  reservationId={occupant.id}
                />
              )}
              <GuestTags
                className={styles.section}
                onUpdate={refetchAllOccupants}
                guestId={occupant.guest.id}
              />
              <NotesToRestaurant
                className={styles.section}
                note={occupant.note || 'N/A'}
              />
              <GuestNotes
                className={styles.section}
                onUpdate={refetchAllOccupants}
                guestId={occupant.guest.id}
              />
              <StaffNote
                className={styles.section}
                reservationId={occupant.id}
                staffNote={occupant.staffNote}
                onUpdate={refetchAllOccupants}
              />
              {floorPlanToDisplay && (
                <>
                  <div className={styles.section}>
                    <OccupantDetailsFloorPlan
                      floorPlan={floorPlanToDisplay}
                      reservation={occupant}
                    />
                    <IconButton
                      iconName="resizeUp"
                      className={styles.expandButton}
                      ariaLabel="expand floor plan"
                      onClick={openExpandFloorPlanModal}
                    />
                  </div>
                  <Modal
                    isOpen={isExpandFloorPlanModalOpen}
                    onClose={closeExpandFloorPlanModal}
                    ariaLabel="floor plan"
                  >
                    <OccupantDetailsFloorPlan
                      floorPlan={floorPlanToDisplay}
                      reservation={occupant}
                    />
                  </Modal>
                </>
              )}
              {allowActivationOfSeatMode &&
                !isReservationFinished(occupant.status) && (
                  <AssignedServer
                    occupant={{ occupantId, occupantType }}
                    server={occupant.server}
                    className={styles.section}
                    onServerUpdateSuccess={refetchOccupant}
                  />
                )}
              {isReservationFinished(occupant.status) && (
                <AssignedServer
                  className={styles.section}
                  emptyStateText="No server was recorded for this reservation"
                  isEditable={false}
                  occupant={{ occupantId, occupantType }}
                  server={occupant.server}
                />
              )}
            </>
          )}

          {isWalkIn(occupant) && (
            <>
              {occupant.guest && (
                <>
                  <GuestTags
                    className={styles.section}
                    onUpdate={refetchAllOccupants}
                    guestId={occupant.guest.id}
                  />

                  <GuestNotes
                    className={styles.section}
                    onUpdate={refetchAllOccupants}
                    guestId={occupant.guest.id}
                  />
                </>
              )}
              {INACTIVE_WALK_IN_STATUSES.includes(occupant.status) && (
                <AssignedServer
                  className={styles.section}
                  emptyStateText="No server was recorded for this walk-in"
                  isEditable={false}
                  occupant={{ occupantId, occupantType }}
                  server={occupant.server}
                />
              )}
            </>
          )}
        </div>

        <div className={styles.column}>
          <OccupantDetailsActions
            occupant={occupant}
            fetchOccupant={refetchOccupant}
            allowActivationOfSeatMode={allowActivationOfSeatMode}
          />

          {isWalkIn(occupant) && (
            <OrderDetails
              order={occupant.lightspeedOrder}
              className={styles.section}
            />
          )}

          {isReservation(occupant) && (
            <>
              <ReservationGuestSheetReceipt
                transactionDetails={occupant.transactionDetails}
                className={styles.section}
              />
              {occupant.cancellationFeeTransaction && (
                <GuestReservationCancellationFee
                  cancellationFee={occupant.cancellationFeeTransaction}
                  className={styles.section}
                />
              )}
              <OrderDetails
                order={occupant.lightspeedOrder}
                className={styles.section}
              />
              {isEnabled('reservationActivityLog') && (
                <>
                  <button
                    className={cx(typography.h7, styles.activityLogButton)}
                    onClick={openActivityLog}
                    type="button"
                  >
                    <Icon name="activityClock" />
                    <span className={styles.activityLogLabel}>
                      Activity Log
                    </span>
                    <Icon
                      name="chevronRight"
                      className={styles.activityLogChevron}
                    />
                  </button>
                  <ActivityLogDrawer
                    isOpen={isActivityLogOpen}
                    onClose={closeActivityLog}
                    reservationId={occupant.id}
                  />
                </>
              )}
              {occupant.systemNote && (
                <section className={styles.section} aria-label="integreatly">
                  <h6 className={typography.h8}>Integreatly</h6>
                  <span className={cx(typography.t2, styles.systemNote)}>
                    {occupant.systemNote}
                  </span>
                </section>
              )}
              {!hasEvent(occupant) && (
                <section aria-label="status" className={styles.statuses}>
                  <ReservationGuestSheetStatuses
                    isHostBooked={
                      !!occupant.transactionDetails.createdBy.fullName
                    }
                    isStranded={occupant.isStranded}
                    reservationStatus={occupant.status}
                  />
                </section>
              )}
              {hasEvent(occupant) && (
                <section className={cx(styles.section, styles.eventSection)}>
                  <dl className={typography.h7}>
                    <div className={cx(styles.eventName)}>
                      <h3 className={typography.h7}>Event</h3>
                      <div>{occupant.eventName}</div>
                    </div>

                    {occupant.listingName && (
                      <div>
                        <dt>Table</dt>
                        <dd>
                          {deriveTableDisplayName({
                            seatedTableName: occupant.seatedTableName,
                            isMultiTable: occupant.isMultiTable,
                            listingName: occupant.listingName,
                          })}
                        </dd>
                      </div>
                    )}

                    <div>
                      <dt>Ticket</dt>
                      <dd>{occupant.eventTierName}</dd>
                    </div>

                    <h3 className={cx(typography.h7_20, styles.addOnHeader)}>
                      Add-Ons
                    </h3>

                    {occupant.purchasedRestaurantEventAddOns.map(
                      (purchasedEventAddOn) => (
                        <div key={purchasedEventAddOn.id}>
                          <dt>{purchasedEventAddOn.name}</dt>
                          <dd>{purchasedEventAddOn.quantity}</dd>
                        </div>
                      ),
                    )}
                  </dl>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
